import type { AccessPolicy, AccessPolicyUpdate } from "./_shared"

import { api } from "@/lib/api-client"

// Route Management
export interface RouteStatus {
  id: string
  name: string
  path: string
  enabled: boolean
  modules: string[]
  /** Route component identifier (e.g. "free" for AI Explorer, "tender" for
   * the Tender module). Used to scope module-specific admin affordances such
   * as the Deep Explorer backend overrides dialog. */
  component?: string
  access_configured?: boolean
  access_overridden?: boolean
}

export type RouteBackendOverrides = {
  custom_instructions?: string
  explorer_deep_max_parallel_subagents?: number | null
  explorer_deep_max_iterations?: number | null
  explorer_deep_smart_followups?: boolean | null
  // Slide-sense module config. Default window for the admin "dead
  // presentations/slides" report. Read by the slide-sense usage endpoint.
  slide_sense_stale_window_days?: number | null
}

export async function getRoutes(): Promise<RouteStatus[]> {
  const response = await api.get("/admin/routes")
  return response.data || response
}

export async function updateRouteStatus(
  routeId: string,
  enabled: boolean
): Promise<{
  success: boolean
  route_id: string
  enabled: boolean
  message: string
}> {
  const response = await api.put(`/admin/routes/${routeId}/status`, { enabled })
  const data = response.data || response

  // Check if the operation actually succeeded
  if (data.success === false) {
    throw new Error(data.message || `Failed to update route ${routeId} status`)
  }

  return data
}

export async function updateRoutesStatusBatch(updates: Record<string, boolean>): Promise<{
  success: boolean
  updated_routes: string[]
  message: string
}> {
  const response = await api.put("/admin/routes/status/batch", { updates })
  const data = response.data || response

  // Check if the operation actually succeeded
  if (data.success === false) {
    throw new Error(data.message || "Failed to batch update route statuses")
  }

  return data
}

export async function getRouteBackendOverrides(routeId: string): Promise<RouteBackendOverrides> {
  const response = await api.get(`/admin/routes/${routeId}/backend-overrides`)
  const data = response.data || response
  return data.backend_overrides || {}
}

export async function updateRouteBackendOverrides(
  routeId: string,
  overrides: RouteBackendOverrides
): Promise<{
  success: boolean
  route_id: string
  backend_overrides: RouteBackendOverrides
  message: string
}> {
  const response = await api.put(`/admin/routes/${routeId}/backend-overrides`, overrides)
  const data = response.data || response
  if (data.success === false) {
    throw new Error(data.message || `Failed to update route ${routeId} backend overrides`)
  }
  return data
}

export async function getRouteAccessPolicy(routeId: string): Promise<AccessPolicy> {
  const response = await api.get(`/admin/routes/${routeId}/access`)
  const data = response.data || response
  return data.access || {}
}

export async function getRouteBaseAccessPolicy(): Promise<AccessPolicy> {
  const response = await api.get("/admin/routes/base/access")
  const data = response.data || response
  return data.access || {}
}

export async function updateRouteAccessPolicy(
  routeId: string,
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put(`/admin/routes/${routeId}/access`, policy)
  const data = response.data || response
  return data.access || {}
}

export async function updateRouteBaseAccessPolicy(
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put("/admin/routes/base/access", policy)
  const data = response.data || response
  return data.access || {}
}

export async function clearRouteAccessPolicy(routeId: string): Promise<AccessPolicy> {
  const response = await api.put(`/admin/routes/${routeId}/access`, {})
  const data = response.data || response
  return data.access || {}
}
